"use client";
import { usePublicSignupEnabled } from "@/components/auth/PublicSignupProvider";
import React, { useRef } from "react";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { signIn } from "next-auth/react";
import { useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { signinFormSchema } from "@/zod/auth.schema";
import LangButton from "@/components/ui/LangButton";
import LabeledInput from "@/components/ui/LabeledInput";
import arrow2 from "@/../public/assets/images/arrow2.svg";
import Image from "next/image";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import toast from "react-hot-toast";
import SignupOverlay from "../../signup/components/SignupOverlay";
import ForgotPasswordOverlay from "../../forgot-password/components/ForgotPasswordOverlay";
import CustomToast from "@/components/ui/CustomToast";
import { AnimatePresence } from "framer-motion";
import { useLocaleState } from "@/hooks/useLocaleState";
import CaptchaField, {
  CaptchaFieldHandle,
} from "@/components/ui/CaptchaField";
import { X } from "lucide-react";

const translations = {
  en: {
    title: "Sign in",
    subtitle: "Use your FARBIN account",
    email: "Email",
    password: "Password",
    submitButton: "Sign In",
    loadingButton: "Signing In...",
    forgotPassword: "Forgot Password?",
    createAccount: "Create account",
    captchaPlaceholder: "Enter the captcha",
    refreshCaptcha: "Refresh captcha",
  },
  fa: {
    title: "ورود به حساب کاربری",
    subtitle: "با استفاده از ایمیل و رمز عبورتان وارد شوید",
    email: "ایمیل",
    password: "رمز عبور",
    submitButton: "ورود",
    loadingButton: "در حال ورود...",
    forgotPassword: "فراموشی رمز عبور؟",
    createAccount: "ایجاد حساب کاربری جدید",
  },
};

function SigninOverlay({ onClose }: { onClose: () => void }) {
  const { changeLocale, currentLang, setCurrentLang } = useLocaleState();
  const [signupOverlay, setSignupOverlay] = useState(false);
  const [forgotOverlay, setForgotOverlay] = useState(false);
  const signupEnabled = usePublicSignupEnabled();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    captcha?: string;
    server?: string;
  }>({});
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const callbackUrl = searchParams.get("callbackUrl") || pathname || "/";
  const t = translations[currentLang];
  const captchaRef = useRef<CaptchaFieldHandle>(null);
  const showCaptcha = true;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    const validationResult = signinFormSchema.safeParse({
      email,
      password,
      captcha: showCaptcha ? captcha : undefined,
    });

    if (!validationResult.success) {
      const { fieldErrors } = validationResult.error.flatten();
      setErrors({
        email: fieldErrors.email?.[0],
        password: fieldErrors.password?.[0],
        captcha: fieldErrors.captcha?.[0],
      });
      setIsLoading(false);
      return;
    }

    try {
      const prepareRes = await fetch("/api/auth/prepare-signin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: validationResult.data.email,
          password: validationResult.data.password,
          captcha: showCaptcha ? validationResult.data.captcha : undefined,
          captchaRequired: showCaptcha,
        }),
      });
      const prepareData = await prepareRes.json().catch(() => ({}));
      if (!prepareRes.ok) {
        setFailedAttempts((prev) =>
          Math.max(prev + 1, prepareData.failedAttempts ?? 0),
        );
        setErrors({
          server: prepareData.message || "An unexpected error occurred. Please try again.",
          captcha:
            prepareData.message === "Captcha verification failed."
              ? "Captcha verification failed."
              : undefined,
        });
        if (prepareData.requireCaptcha) {
          captchaRef.current?.reset();
          setCaptcha("");
        }
        return;
      }

      const res = await signIn("credentials", {
        email: validationResult.data.email,
        password: validationResult.data.password,
        signinGuard: prepareData.signinGuard,
        redirect: false,
      });
      if (res?.error) {
        setFailedAttempts((prev) => Math.max(prev, 2));
        setErrors({ server: res.error });
        captchaRef.current?.reset();
        setCaptcha("");
      } else {
        setFailedAttempts(0);
        toast.custom((t) => (
          <CustomToast t={t} message="Login successful." type="success" />
        ));
        router.replace(callbackUrl);
        router.refresh();
      }
    } catch (error) {
      console.log(error);
      setFailedAttempts((prev) => prev + 1);
      setErrors({ server: "An unexpected error occurred. Please try again." });
      captchaRef.current?.reset();
      setCaptcha("");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <OverlayWrapper
      onClose={onClose}
      snapPoints={["93vh"]}
      className="bg-grayCustom"
      draggable={false}
    >
      <AnimatePresence>
        {/* overlays */}
        {signupEnabled && signupOverlay && (
          <SignupOverlay onClose={() => setSignupOverlay(false)} />
        )}
        {forgotOverlay && (
          <ForgotPasswordOverlay
            onClose={() => setForgotOverlay(false)}
            onPrefillEmail={(em) => setEmail(em)}
            defaultEmail={email}
          />
        )}
      </AnimatePresence>
      <div className="flex h-full items-start justify-center px-2">
        <div className="max-w-md w-full rounded p-6 ">
          <div className="flex justify-between mb-10">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slateCustom transition hover:text-pinkCustom"
            >
              <X className="h-4 w-4" strokeWidth={4} />
            </button>
            <LangButton
              currentLang={currentLang}
              setCurrentLang={setCurrentLang}
              bgColor="white"
              onLocaleChange={changeLocale}
            />
          </div>
          <h1
            dir={currentLang === "fa" ? "rtl" : "ltr"}
            className="relative text-xl font-semibold leading-tight mt-0 mb-8"
          >
            {t.title}
            <div
              className={`absolute ${
                currentLang === "fa" ? "-right-3.5" : "-left-3.5"
              } bottom-6.5`}
            >
              <Image
                className="w-3"
                src={arrow2}
                alt="arrow"
                style={{
                  transform: currentLang === "fa" ? "scaleX(-1)" : "none",
                }}
              />
            </div>
            <p className="text-xs font-light text-gray-400 mt-2">
              {t.subtitle}
            </p>
          </h1>
          <form onSubmit={handleSubmit} className="space-y-4">
            <LabeledInput
              label={t.email}
              name="email"
              type="email"
              value={email}
              className="w-full bg-white"
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              disabled={isLoading}
            />

            <LabeledInput
              type="password"
              label={t.password}
              name="password"
              value={password}
              className="w-full bg-white"
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              disabled={isLoading}
            />
            {showCaptcha && (
              <CaptchaField
                ref={captchaRef}
                value={captcha}
                onChange={setCaptcha}
                onClearError={() =>
                  setErrors((prev) => ({ ...prev, captcha: undefined }))
                }
                disabled={isLoading}
                error={errors.captcha}
                placeholder="Enter the captcha"
                refreshLabel="Refresh captcha"
              />
            )}
            <button
              className="bg-slateCustom w-full text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <DotPulseLdr size={40} speed={1.3} color="white" />
              ) : (
                t.submitButton
              )}
            </button>
            <div className="flex items-center justify-end">
              <button
                type="button"
                className="inline-block align-baseline font-normal text-slateCustom"
                onClick={() => setForgotOverlay(true)}
              >
                {t.forgotPassword}
              </button>
            </div>
            {errors.server && (
              <p className="text-red-500 text-center text-sm">
                {errors.server}
              </p>
            )}
          </form>
          {signupEnabled && (<div className="mt-10">
            <button
              onClick={() => {
                setSignupOverlay(true);
              }}
            >
              <p className=" text-slateCustom">{t.createAccount}</p>
            </button>
          </div>)}
        </div>
      </div>
    </OverlayWrapper>
  );
}

export default SigninOverlay;
